/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { Row, Container, Col, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import styles from './PasswordReset.module.css';
import { Credentials } from 'model/Credentials';
import Message from 'containers/common/Message';
import { FloButton, FloFormGroup, FloFormControl } from 'components';
import { useParams, Link } from 'react-router-dom';
import classnames from 'classnames';


type PasswordResetProps = {
  isValidatingToken: boolean,
  hasError: boolean,
  isSubmitting: boolean,
  submittedOK: boolean,
  validatePasswordResetToken: (userId: string, token: string) => void,
  resetToken?: string,
  resetPassword: (credentials: Credentials, token: string) => void,
}

export const PasswordReset: React.FC<PasswordResetProps> = React.memo(({
  isValidatingToken, hasError, isSubmitting, submittedOK, validatePasswordResetToken, resetPassword, resetToken,
}) => {

  const { t } = useTranslation();
  const urlParams = useParams<{ userId: string, resetToken: string }>();

  useEffect(() => {
    validatePasswordResetToken(urlParams.userId, urlParams.resetToken);
  }, []);


  return (
    <Container>
      <Row>
        <Col className={styles.logo} >
          <img alt="Flo Multi-Unit Dashboard" src="/images/MoenSmartLogo.png" />
        </Col>
      </Row>
      <Row>
        <Col className={styles.title}>
          {t('Multi-Unit Dashboard Password Reset')}
        </Col>
      </Row>
      <Row
        className={classnames(
          styles.root,
          { [styles.isLoading]: isValidatingToken }
        )}
      >
        { isValidatingToken &&
        <Col xs="auto" >
            <Spinner className={styles.loader} animation="border" role="status">
                <span className="sr-only">Loading...</span>
            </Spinner>
        </Col>
        }
        { !isValidatingToken &&
        <Col
            className={classnames(
              styles.formWrapper,
              { [styles.expirationError]: (hasError || submittedOK)}
            )}
            xs="12"
            md={{ span: 6, offset: 3 }}
        >
          <Message />

          { submittedOK && <div className={styles.loginRedirect}><Link to="/login">{t('go_to_login')}</Link></div> }

          { !hasError && !submittedOK &&
          <Formik
            enableReinitialize
            initialValues={{
              password: '',
              passwordConfirmation: '',
            }}
            onSubmit={(values, actions) => {
              resetPassword({ username: '', password: values.password }, resetToken as string);
              actions.setSubmitting(false);
            }}
            validationSchema={Yup.object().shape({
              password: Yup.string().required(t('validation_required', { field: t('Password') }))
                .min(8, t('min_password'))
                .matches(/[a-z]/, t('lowercase_required'))
                .matches(/[A-Z]/, t('uppercase_required'))
                .matches(/[0-9]/, t('number_required'))
                .matches(/[!@#$%^&*)(+=._-]/, t('special_char_required')),
              passwordConfirmation: Yup.string().when("password", {
                is: val => (val && val.length > 0 ? true : false),
                then: Yup.string().oneOf(
                  [Yup.ref("password")], t('password_not_match'))
              }).required(t('validation_required', { field: t('Confirm Password') })),
            })}
          >
            {({ values, handleBlur, handleChange, errors, touched, isValid, handleSubmit, setFieldValue, setFieldTouched }) => {

              return (
                <Form className={styles.form} onSubmit={handleSubmit}>

                  <Row>
                    <Col xs="12">
                      <FloFormGroup
                        controlId   = "password"
                        label       = {t('Password')}
                        touched     = {touched}
                        errors      = {errors}
                      >
                        <FloFormControl
                          type="password"
                          name="password"
                          placeholder={t('Password')}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.password}
                          disabled={isSubmitting}
                          isInvalid={!!(touched.password && errors.password)}
                        />
                      </FloFormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs="12">
                      <FloFormGroup
                        controlId   = "passwordConfirmation"
                        label       = {t('Confirm Password')}
                        touched     = {touched}
                        errors      = {errors}
                      >
                        <FloFormControl
                          type="password"
                          name="passwordConfirmation"
                          placeholder={t('Confirm Password')}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.passwordConfirmation}
                          disabled={isSubmitting}
                          isInvalid={!!(touched.passwordConfirmation && errors.passwordConfirmation)}
                        />
                      </FloFormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs="12">
                      <FloButton
                        className   ={styles.submitButton}
                        text        = { t('registration_confirm_button') }
                        onClick     = {() => {}}
                        disabled    = {!isValid || isSubmitting}
                        showLoader  = {isSubmitting}
                        fullWidth
                        submit
                      />
                    </Col>
                  </Row>

                </Form>
              )
            }}
          </Formik>
          }
        </Col>
        }
      </Row>
    </Container>
  );
});

export default PasswordReset;
